/**
 * 価格計算システム - バリエーション選択モジュール
 * @version 2.1.0
 *
 * 責務:
 * - バリエーション選択UIの生成
 * - 用紙・印刷色数・ページ数セクションの初期化
 * - ページ範囲セレクターの初期化
 * - サイズセレクターの初期化
 */
(function() {
  'use strict';

  window.PriceCalculator = window.PriceCalculator || {};

  /**
   * バリエーション選択モジュール
   */
  const VariantSelectors = {
    /**
     * バリエーション選択UIを初期化
     */
    initialize: function() {
      const vsStartTime = performance.now();
      let vsLastTime = vsStartTime;
      const vsLogTiming = (label) => {
        const now = performance.now();
        console.log(`[VS Timing] ${label}: ${(now - vsLastTime).toFixed(2)}ms`);
        vsLastTime = now;
      };

      const variations = window.productData.price_matrix.variations || {};
      const variationKeys = Object.keys(variations);
      const pricingConfigData = window.pricingConfigData;
      const shopifyOptions = window.productData.shopify_options || [];

      // 完全遅延ロードモードの場合、先にAPIからデータをロード
      if (window.needsLazyLoad && variationKeys.length === 0) {
        return this.handleLazyLoad();
      }

      if (variationKeys.length === 0) {
        console.warn('[VariantSelectors] No variations found');
        return;
      }

      // Shopifyオプションからページ数/ページ範囲を検出
      const pageCountOption = shopifyOptions.find(opt =>
        opt.name === 'ページ数' || opt.name === 'ページ範囲'
      );

      // variation_code_configを確認（size_only判定用）
      const variationConfig = window.productData?.variation_code_config;
      const isSizeOnlyFormat = variationConfig?.format === 'size_only';

      // 伝票印刷（size_only）の場合はサイズオプションを用紙として扱う
      const paperOption = isSizeOnlyFormat
        ? shopifyOptions.find(opt => opt.name.includes('サイズ') || opt.name.toLowerCase().includes('size'))
        : shopifyOptions.find(opt => opt.name === '用紙');
      const printColorOption = shopifyOptions.find(opt => opt.name === '印刷色数');


      // ページ範囲セクションは常に非表示
      this.hidePageRangeSection();

      // 用紙と色数を抽出
      const { papers, printColors } = this.extractPaperAndColors(
        paperOption, printColorOption, variationKeys, pageCountOption
      );
      vsLogTiming('extractPaperAndColors');


      // 印刷サイズ選択の初期化
      this.initializeSizeSelector(pricingConfigData);
      vsLogTiming('initializeSizeSelector');

      // ★ paper_selector_mode チェック（統合モードはBookletモジュールが処理）
      const paperSelectorMode = window.productData?.paper_selector_mode;
      // ★ 本文紙質がある冊子は Booklet が coverPaper/bodyPaper を管理するため標準用紙を隠す。
      //   ページ範囲 + 用紙（無線綴じオンデマンド）は標準の用紙セクションを使う。
      const hasBodyPaperOptionEarly = shopifyOptions.some(opt => opt.name === '本文紙質');
      const hasPageRangeOptionEarly = shopifyOptions.some(opt => opt.name === 'ページ範囲');
      const hasPaperOptionEarly = shopifyOptions.some(opt => opt.name === '用紙');
      const isBodyPaperBookletEarly = hasBodyPaperOptionEarly;
      const isPageRangeWithPaperEarly = hasPageRangeOptionEarly && hasPaperOptionEarly && !hasBodyPaperOptionEarly;

      if (paperSelectorMode === 'unified' || paperSelectorMode === 'monochrome_unified') {
        console.log('[VariantSelectors] Unified paper mode - skipping paper/color sections (Booklet handles this)');
      } else if (isBodyPaperBookletEarly) {
        console.log('[VariantSelectors] Body-paper booklet mode - skipping paper/color sections (Booklet handles this)');
        const paperSection = document.getElementById('paper-section');
        if (paperSection) paperSection.style.display = 'none';
        const printColorSection = document.getElementById('print-color-section');
        if (printColorSection) printColorSection.style.display = 'none';
      } else if (isPageRangeWithPaperEarly) {
        // オンデマンド無線綴じなど: 用紙は選択可能、印刷色数は固定値が多いので非表示
        console.log('[VariantSelectors] Page-range + paper mode - initializing paper section');
        this.initializePaperAndColorSections(papers, printColors);
        const printColorSection = document.getElementById('print-color-section');
        if (printColorSection) printColorSection.style.display = 'none';
      } else if (hasPageRangeOptionEarly) {
        console.log('[VariantSelectors] Page-range booklet mode - hiding paper/color sections');
        const paperSection = document.getElementById('paper-section');
        if (paperSection) paperSection.style.display = 'none';
        const printColorSection = document.getElementById('print-color-section');
        if (printColorSection) printColorSection.style.display = 'none';
      } else {
        // 用紙・色数セクションの初期化（標準商品用）
        this.initializePaperAndColorSections(papers, printColors);
      }
      vsLogTiming('initializePaperAndColorSections');

      // 伝票印刷（size_only）の場合は印刷色数セクションを非表示
      // （固定オプションとしてLiquid側で別表示するため）
      if (isSizeOnlyFormat) {
        const printColorSection = document.getElementById('print-color-section');
        if (printColorSection) printColorSection.style.display = 'none';
      }

      // ページ数選択の初期化
      this.initializePageCountSelector(shopifyOptions);
      vsLogTiming('initializePageCountSelector');

      // ページ範囲セレクターの初期化（Shopifyオプションにページ範囲がある場合）
      this.initializePageRangeSelector();
      vsLogTiming('initializePageRangeSelector');

      // ハーフカットセレクターの初期化（ステッカー商品用）
      this.initializeHalfCutSelector();
      vsLogTiming('initializeHalfCutSelector');

      // 無線綴じ冊子用セレクターの初期化
      console.log('[VariantSelectors] Checking Booklet:', {
        hasBooklet: !!window.PriceCalculator.Booklet,
        shopifyOptionsLength: shopifyOptions.length,
        variationKeysLength: variationKeys.length
      });

      // Bookletモード判定
      // - 本文紙質あり: オフセット冊子等（印刷色数セクション非表示）
      // - ページ範囲 + 用紙: オンデマンド冊子（用紙は表示済み、印刷色数は非表示）
      const hasBodyPaperOption = shopifyOptions.some(opt => opt.name === '本文紙質');
      const hasPageRangeOption = shopifyOptions.some(opt => opt.name === 'ページ範囲');
      const isBodyPaperBooklet = hasBodyPaperOption;
      const isPageRangeBooklet = hasPageRangeOption;

      if (window.PriceCalculator.Booklet) {
        window.PriceCalculator.Booklet.initialize(shopifyOptions, variationKeys);
      } else {
        console.warn('[VariantSelectors] PriceCalculator.Booklet not available');
      }
      vsLogTiming('Booklet.initialize');

      // 冊子系は印刷色数セクションを非表示（固定 or Booklet管理）
      if (isBodyPaperBooklet || isPageRangeBooklet) {
        const printColorSection = document.getElementById('print-color-section');
        if (printColorSection) {
          printColorSection.style.display = 'none';
          console.log('[VariantSelectors] Booklet/page-range mode - hiding print-color-section');
        }
      }

      // 色数選択ボタンの初期化（本文紙質冊子・ページ範囲商品以外）
      if (!isBodyPaperBooklet && !isPageRangeBooklet) {
        this.initializePrintColorButton(papers, printColors);
      }
      vsLogTiming('initializePrintColorButton');

      console.log(`[VariantSelectors] Initialize completed in ${(performance.now() - vsStartTime).toFixed(2)}ms`);
    },

    /**
     * 遅延ロードの処理
     * @returns {boolean} false to indicate early return
     */
    handleLazyLoad: function() {
      if (!window.productData || !window.productData.variants || window.productData.variants.length === 0) {
        return false;
      }

      // 既に全データが読み込まれている場合はAPIからの読み込みをスキップ
      const existingPriceData = window.productData?.price_matrix?.price_data || [];
      const existingVariationCodes = [...new Set(existingPriceData.map(p => p.variation_code))];

      if (existingVariationCodes.length > 1) {
        console.log('[VariantSelectors] handleLazyLoad - Data already loaded, performing full initialization');
        // 全データが既にある場合は完全な初期化を実行
        this.performFullInitializationFromPriceData();
        return false;
      }

      const variants = window.productData.variants;
      const shopifyOptions = window.productData.shopify_options || [];

      // Shopifyオプションから印刷色数オプションを検出
      const printColorOption = shopifyOptions.find(opt => opt.name === '印刷色数');
      const hasPrintColorOption = printColorOption && printColorOption.values && printColorOption.values.length > 0;

      let targetVariant;

      if (hasPrintColorOption) {
        // チラシ商品: 「片面１色」を含むバリアントを優先（基本的な印刷タイプ）
        const preferredPrintColor = '片面１色';
        targetVariant = variants.find(v => v.option1 === preferredPrintColor || v.option2 === preferredPrintColor);

        if (!targetVariant) {
          // 「片面１色」がなければ、option1が印刷色数なら最初のものを選択
          targetVariant = variants[0];
        }
      } else {
        // 冊子商品など: ページ範囲でソート
        const sortedVariants = [...variants].sort((a, b) => {
          const matchA = (a.option1 || '').match(/(\d+)/);
          const matchB = (b.option1 || '').match(/(\d+)/);
          const numA = matchA ? parseInt(matchA[1]) : 9999;
          const numB = matchB ? parseInt(matchB[1]) : 9999;
          return numA - numB;
        });
        targetVariant = sortedVariants[0] || variants[0];
      }

      if (targetVariant && targetVariant.id && typeof window.loadVariantPriceMatrix === 'function') {
        window.loadVariantPriceMatrix(targetVariant.id);
      }
      return false;
    },

    /**
     * 価格データから完全な初期化を実行
     * （遅延ロード後に呼び出される）
     */
    performFullInitializationFromPriceData: function() {
      const shopifyOptions = window.productData?.shopify_options || [];

      // variation_code_configを確認（size_only判定用）
      const variationConfig = window.productData?.variation_code_config;
      const isSizeOnlyFormat = variationConfig?.format === 'size_only';

      // 伝票印刷（size_only）の場合はサイズオプションを用紙として扱う
      const paperOption = isSizeOnlyFormat
        ? shopifyOptions.find(opt => opt.name.includes('サイズ') || opt.name.toLowerCase().includes('size'))
        : shopifyOptions.find(opt => opt.name === '用紙');
      const printColorOption = shopifyOptions.find(opt => opt.name === '印刷色数');
      const ValueOrder = window.PriceCalculator?.ValueOrder;

      // 価格データから利用可能な用紙と印刷色数を抽出
      const papers = new Set();
      const printColors = new Set();

      // Shopifyオプションから抽出（優先）
      if (paperOption && paperOption.values) {
        paperOption.values.forEach(v => papers.add(v));
      }
      if (printColorOption && printColorOption.values) {
        printColorOption.values.forEach(v => printColors.add(v));
      }

      // 用紙・印刷色数の初期設定
      this.setInitialPrintColorOnly();

      // 用紙セクションの表示と有効化
      const paperSection = document.getElementById('paper-section');
      const printColorSection = document.getElementById('print-color-section');

      if (papers.size > 0 && paperSection) {
        paperSection.style.display = '';
      }
      // 伝票印刷（size_only）の場合は印刷色数セクションを非表示
      if (isSizeOnlyFormat) {
        if (printColorSection) printColorSection.style.display = 'none';
      } else if (printColors.size > 0 && printColorSection) {
        printColorSection.style.display = '';
      }

      // 用紙ボタンのクリックハンドラーを設定
      // size_only（伝票印刷）の場合、currentSelection.sizeも設定
      const currentPaper = window.priceCalculator?.currentSelection?.paper;
      if (isSizeOnlyFormat && currentPaper) {
        window.priceCalculator.currentSelection.size = currentPaper;
        // ヘッダーのサイズ表示を更新
        const headerSizeDisplay = document.getElementById('header-size-display');
        if (headerSizeDisplay) {
          headerSizeDisplay.textContent = 'サイズ: ' + currentPaper;
        }
      }
      this.setupPaperButton(papers, currentPaper, isSizeOnlyFormat);

      // 印刷色数ボタンのクリックハンドラーを設定
      this.initializePrintColorButton(papers, printColors);

      // ページ数セレクターの初期化
      this.initializePageCountSelector(shopifyOptions);

      console.log('[VariantSelectors] performFullInitializationFromPriceData completed');
    },

    /**
     * ページ範囲セクションを非表示
     */
    hidePageRangeSection: function() {
      const pageCountVariantSection = document.getElementById('page-count-variant-section');
      if (pageCountVariantSection) {
        pageCountVariantSection.style.display = 'none';
      }
    },

    /**
     * 用紙と印刷色を抽出
     */
    extractPaperAndColors: function(paperOption, printColorOption, variationKeys, pageCountOption) {
      const papers = new Set();
      const printColors = new Set();

      // Shopifyオプションから抽出（優先）
      if (paperOption && paperOption.values) {
        paperOption.values.forEach(v => papers.add(v));
      }
      if (printColorOption && printColorOption.values) {
        printColorOption.values.forEach(v => printColors.add(v));
      }

      // fallback 1: variationKeysから抽出
      if (papers.size === 0 && printColors.size === 0 && !pageCountOption) {
        variationKeys.forEach(key => {
          if (key === '標準') return;

          if (key.includes('_')) {
            const parts = key.split('_');
            if (parts.length >= 2) {
              papers.add(parts[0]);
              printColors.add(parts.slice(1).join('_'));
            }
          } else {
            papers.add(key);
          }
        });
      }

      // fallback 2: price_matrix.price_dataのvariation_codeから抽出（正規化形式の商品用）
      if (papers.size === 0 && printColors.size === 0) {
        const priceData = window.productData?.price_matrix?.price_data || [];
        const uniqueVariationCodes = [...new Set(priceData.map(p => p.variation_code).filter(Boolean))];

        uniqueVariationCodes.forEach(code => {
          if (!code || code === '標準') return;

          const parts = code.split('_');
          if (parts.length >= 2) {
            papers.add(parts[0]);
            printColors.add(parts.slice(1).join('_'));
          } else if (parts.length === 1) {
            papers.add(parts[0]);
          }
        });

        if (papers.size > 0 || printColors.size > 0) {
          console.log('[VariantSelectors] Extracted from price_matrix.price_data:', {
            papers: Array.from(papers),
            printColors: Array.from(printColors)
          });
        }
      }

      return { papers, printColors };
    },

    /**
     * サイズセレクターの初期化
     */
    initializeSizeSelector: function(pricingConfigData) {
      const printSizeSection = document.getElementById('print-size-section');

      if (!pricingConfigData || !pricingConfigData.size_options || !pricingConfigData.size_options.enabled) {
        if (printSizeSection) printSizeSection.style.display = 'none';
        return;
      }

      const sizes = pricingConfigData.size_options.sizes || [];
      if (sizes.length === 0) {
        if (printSizeSection) printSizeSection.style.display = 'none';
        return;
      }

      if (printSizeSection) printSizeSection.style.display = '';

      const urlParams = new URLSearchParams(window.location.search);
      const urlSize = urlParams.get('size');
      const defaultSize = sizes[0]?.value || sizes[0];
      const currentSize = urlSize || window.priceCalculator.currentSelection.size || defaultSize;

      window.priceCalculator.currentSelection.size = currentSize;

      const printSizeBtn = document.getElementById('print-size-select-btn');
      const printSizeValueSpan = document.getElementById('print-size-current-value');

      if (!printSizeBtn || !printSizeValueSpan) return;

      const currentSizeObj = sizes.find(s => s.value === currentSize || s === currentSize);
      const displayLabel = currentSizeObj?.label || currentSize + 'サイズ';
      printSizeValueSpan.textContent = displayLabel;

      if (sizes.length <= 1) {
        printSizeBtn.style.opacity = '0.7';
        printSizeBtn.style.cursor = 'default';
        printSizeBtn.onclick = null;
      } else {
        printSizeBtn.style.opacity = '';
        printSizeBtn.style.cursor = 'pointer';
        printSizeBtn.onclick = () => {
          const latestSize = window.priceCalculator.currentSelection.size || defaultSize;
          const sizeLabels = sizes.map(s => s.label || s + 'サイズ');
          if (window.PriceCalculator.Modal) {
            window.PriceCalculator.Modal.open('印刷サイズ', sizeLabels, latestSize + 'サイズ', window.selectPrintSize);
          }
        };
      }
    },

    /**
     * 用紙・印刷色セクションの初期化
     */
    initializePaperAndColorSections: function(papers, printColors) {
      const paperSection = document.getElementById('paper-section');
      const printColorSection = document.getElementById('print-color-section');

      if (papers.size === 0) {
        if (paperSection) paperSection.style.display = 'none';
        if (printColorSection) printColorSection.style.display = 'none';
        return;
      }

      if (paperSection) paperSection.style.display = '';

      if (printColors.size === 0) {
        if (printColorSection) printColorSection.style.display = 'none';
      } else {
        if (printColorSection) printColorSection.style.display = '';
      }

      // 用紙に対応する有効な印刷色数を取得（価格データがある用紙のみ）
      const combinations = window.availableVariantCombinations || window.productData?.availableVariantCombinations;

      // 価格データがある用紙のみにフィルタリング
      // withPriceByPaperに含まれる用紙 = 実際に価格が設定されている用紙
      let sortedPapers = Array.from(papers);
      if (combinations && combinations.withPriceByPaper) {
        const papersWithPrice = new Set(Object.keys(combinations.withPriceByPaper));
        sortedPapers = sortedPapers.filter(p => papersWithPrice.has(p));
        if (sortedPapers.length === 0) {
          // 価格データがある用紙がない場合は元のリストを使用（フォールバック）
          sortedPapers = Array.from(papers);
          console.warn('[VariantSelectors] No papers with price data found, using all papers');
        }
      }

      // 用紙リストを並び順マスタでソート（PrintHubで設定された順序を適用）
      const ValueOrder = window.PriceCalculator?.ValueOrder;
      if (ValueOrder && ValueOrder.isLoaded()) {
        sortedPapers = ValueOrder.sortValues(sortedPapers, '用紙');
      }

      // 現在の用紙を取得（並び順マスタの最初の値をデフォルトにする）
      const currentPaper = window.priceCalculator.currentSelection.paper || sortedPapers[0];
      let validPrintColorsForPaper = printColors.size > 0 ? Array.from(printColors) : [];

      if (combinations && combinations.byPaper && combinations.byPaper[currentPaper]) {
        validPrintColorsForPaper = combinations.byPaper[currentPaper];
      }

      // 印刷色数リストを並び順マスタでソート（PrintHubで設定された順序を適用）
      if (ValueOrder && ValueOrder.isLoaded() && validPrintColorsForPaper.length > 0) {
        validPrintColorsForPaper = ValueOrder.sortValues(validPrintColorsForPaper, '印刷色数');
      }

      // 現在の印刷色数を取得
      let currentColor = this.determineCurrentPrintColor(validPrintColorsForPaper);

      // variation_code_configを確認（size_only判定用）
      const variationConfig = window.productData?.variation_code_config;
      const isSizeOnlyFormat = variationConfig?.format === 'size_only';

      if (!window.priceCalculator.currentSelection.paper && currentPaper) {
        window.priceCalculator.currentSelection.paper = currentPaper;
      }

      // size_only（伝票印刷）の場合、currentSelection.sizeも設定
      if (isSizeOnlyFormat && currentPaper) {
        window.priceCalculator.currentSelection.size = currentPaper;
        // ヘッダーのサイズ表示を更新
        const headerSizeDisplay = document.getElementById('header-size-display');
        if (headerSizeDisplay) {
          headerSizeDisplay.textContent = 'サイズ: ' + currentPaper;
        }
      }

      if (validPrintColorsForPaper.length > 0 && currentColor) {
        window.priceCalculator.currentSelection.printColor = currentColor;
      }
      if (validPrintColorsForPaper.length === 0) {
        window.priceCalculator.currentSelection.printColor = null;
      }

      // 用紙ボタンの設定（size_onlyフラグを渡す）
      this.setupPaperButton(papers, currentPaper, isSizeOnlyFormat);
    },

    /**
     * 現在の印刷色を決定
     */
    determineCurrentPrintColor: function(validPrintColorsForPaper) {
      if (validPrintColorsForPaper.length === 0) return null;

      const existingPrintColor = window.priceCalculator.currentSelection.printColor;
      if (existingPrintColor && validPrintColorsForPaper.includes(existingPrintColor)) {
        return existingPrintColor;
      }

      const newColor = validPrintColorsForPaper[0];
      if (existingPrintColor) {
      }
      return newColor;
    },

    /**
     * 既にデータがある場合に用紙・印刷色数を初期設定
     * （API読み込みをスキップした場合に使用）
     */
    setInitialPrintColorOnly: function() {
      const shopifyOptions = window.productData?.shopify_options || [];

      // variation_code_configを確認（size_only判定用）
      const variationConfig = window.productData?.variation_code_config;
      const isSizeOnlyFormat = variationConfig?.format === 'size_only';

      const printColorOption = shopifyOptions.find(opt => opt.name === '印刷色数');
      // 伝票印刷（size_only）の場合はサイズオプションを用紙として扱う
      const paperOption = isSizeOnlyFormat
        ? shopifyOptions.find(opt => opt.name.includes('サイズ') || opt.name.toLowerCase().includes('size'))
        : shopifyOptions.find(opt => opt.name === '用紙');
      const ValueOrder = window.PriceCalculator?.ValueOrder;

      if (!window.priceCalculator) return;

      // 現在の用紙を取得（価格データがある用紙のみ、並び順マスタの最初の値をデフォルトにする）
      let currentPaper = window.priceCalculator.currentSelection.paper;
      if (!currentPaper && paperOption && paperOption.values && paperOption.values.length > 0) {
        const combinations = window.availableVariantCombinations || window.productData?.availableVariantCombinations;
        // 価格データがある用紙のみを使用
        let validPapers = [...paperOption.values];
        if (combinations && combinations.withPriceByPaper) {
          validPapers = paperOption.values.filter(p => combinations.withPriceByPaper[p]);
          if (validPapers.length === 0) {
            // フォールバック: 全バリアントの組み合わせ
            validPapers = [...paperOption.values];
          }
        }
        if (ValueOrder && ValueOrder.isLoaded()) {
          validPapers = ValueOrder.sortValues(validPapers, '用紙');
        }
        currentPaper = validPapers[0];
        window.priceCalculator.currentSelection.paper = currentPaper;
      }

      // 印刷色数の設定
      let currentPrintColor = window.priceCalculator.currentSelection.printColor;
      if (printColorOption && printColorOption.values && printColorOption.values.length > 0) {
        const combinations = window.availableVariantCombinations || window.productData?.availableVariantCombinations;
        let validPrintColors = [...printColorOption.values];

        // 組み合わせデータがあれば、現在の用紙で価格データがある印刷色数のみを使用
        // withPriceByPaper を優先（price_matrixに存在する組み合わせのみ）
        if (combinations && combinations.withPriceByPaper && combinations.withPriceByPaper[currentPaper]) {
          validPrintColors = combinations.withPriceByPaper[currentPaper];
        } else if (combinations && combinations.byPaper && combinations.byPaper[currentPaper]) {
          // フォールバック: 全バリアントの組み合わせ
          validPrintColors = combinations.byPaper[currentPaper];
        }

        // 並び順マスタでソート（PrintHubで設定された順序を適用）
        if (ValueOrder && ValueOrder.isLoaded()) {
          validPrintColors = ValueOrder.sortValues(validPrintColors, '印刷色数');
        }

        if (!currentPrintColor || !validPrintColors.includes(currentPrintColor)) {
          // デフォルトは並び順マスタの最初の値、またはフォールバックで「片面１色」
          const defaultColor = validPrintColors[0] || (validPrintColors.includes('片面１色') ? '片面１色' : validPrintColors[0]);
          window.priceCalculator.currentSelection.printColor = defaultColor;
          currentPrintColor = defaultColor;
        }
      }

      // 用紙ボタンの表示を更新
      const paperValueSpan = document.getElementById('paper-current-value');
      if (paperValueSpan && currentPaper) {
        paperValueSpan.classList.remove('skeleton-loading');
        paperValueSpan.textContent = currentPaper;
      }

      // 印刷色数ボタンの表示を更新
      const printColorValueSpan = document.getElementById('print-color-current-value');
      if (printColorValueSpan && currentPrintColor) {
        printColorValueSpan.classList.remove('skeleton-loading');
        printColorValueSpan.textContent = currentPrintColor;
      }

      console.log('[VariantSelectors] setInitialPrintColorOnly completed:', {
        paper: currentPaper,
        printColor: currentPrintColor
      });
    },

    /**
     * 価格データから利用可能な用紙リストを取得
     * @returns {Array} 価格データに存在する用紙のリスト
     */
    getAvailablePapersFromPriceData: function() {
      const priceData = window.productData?.price_matrix?.price_data || [];
      if (priceData.length === 0) {
        console.log('[VariantSelectors] No price_data available');
        return null;
      }

      const availablePapers = new Set();
      priceData.forEach(entry => {
        if (entry.variation_code) {
          // variation_code から用紙名を抽出（最初のパート）
          const parts = entry.variation_code.split('_');
          if (parts.length > 0) {
            availablePapers.add(parts[0]);
          }
        }
      });

      console.log('[VariantSelectors] getAvailablePapersFromPriceData:', {
        priceDataLength: priceData.length,
        availablePapersCount: availablePapers.size,
        papers: Array.from(availablePapers).sort()
      });

      return availablePapers.size > 0 ? Array.from(availablePapers) : null;
    },

    /**
     * 指定した用紙で利用可能な印刷色数を価格データから取得
     * @param {string} paper - 用紙名
     * @returns {Array} 利用可能な印刷色数のリスト
     */
    getAvailablePrintColorsForPaper: function(paper) {
      // availableVariantCombinationsを優先的に使用（Lazy Load後の完全なデータ）
      // withPriceByPaper を優先（価格データがある組み合わせのみ）
      const combinations = window.availableVariantCombinations || window.productData?.availableVariantCombinations;
      if (combinations && combinations.withPriceByPaper && combinations.withPriceByPaper[paper]) {
        return combinations.withPriceByPaper[paper];
      }
      // フォールバック: 全バリアントの組み合わせ
      if (combinations && combinations.byPaper && combinations.byPaper[paper]) {
        return combinations.byPaper[paper];
      }

      // フォールバック: price_dataから抽出
      const priceData = window.productData?.price_matrix?.price_data || [];
      if (priceData.length === 0) return null;

      const availableColors = new Set();
      priceData.forEach(entry => {
        if (entry.variation_code && entry.variation_code.startsWith(paper + '_')) {
          // variation_code から印刷色数を抽出（2番目のパート）
          const parts = entry.variation_code.split('_');
          if (parts.length > 1) {
            availableColors.add(parts.slice(1).join('_'));
          }
        }
      });

      return availableColors.size > 0 ? Array.from(availableColors) : null;
    },

    /**
     * 用紙ボタンの設定
     * @param {Set} papers - 利用可能な用紙/サイズ
     * @param {string} currentPaper - 現在の用紙/サイズ
     * @param {boolean} isSizeOnly - size_onlyフォーマットかどうか
     */
    setupPaperButton: function(papers, currentPaper, isSizeOnly = false) {
      const paperBtn = document.getElementById('paper-select-btn');
      const paperValueSpan = document.getElementById('paper-current-value');
      const paperLabelSpan = document.getElementById('paper-option-label');

      if (!paperBtn || !paperValueSpan) return;

      paperValueSpan.classList.remove('skeleton-loading');
      paperValueSpan.textContent = currentPaper;

      // 用紙オプションの表示名を取得（メタオブジェクトから）
      // size_only（伝票印刷）の場合は「サイズ」を使用
      const ValueOrder = window.PriceCalculator?.ValueOrder;
      const paperDisplayName = isSizeOnly
        ? 'サイズ'
        : (ValueOrder?.getDisplayName?.('用紙', Array.from(papers)) || '用紙');

      // サイドバーのラベルを更新
      if (paperLabelSpan) {
        paperLabelSpan.textContent = paperDisplayName;
      }

      if (papers.size <= 1) {
        paperBtn.style.opacity = '0.7';
        paperBtn.style.cursor = 'default';
        paperBtn.onclick = null;
      } else {
        paperBtn.style.opacity = '';
        paperBtn.style.cursor = 'pointer';
        paperBtn.onclick = () => {
          const latestPaper = window.priceCalculator.currentSelection.paper || currentPaper;

          // Shopifyの用紙オプションを取得
          let filteredPapers = Array.from(papers);

          // 価格データがある用紙のみにフィルタリング
          // （非表示化された用紙を除外）
          const combinations = window.availableVariantCombinations || window.productData?.availableVariantCombinations;
          if (combinations && combinations.withPriceByPaper) {
            const papersWithPrice = new Set(Object.keys(combinations.withPriceByPaper));
            const papersWithPriceFiltered = filteredPapers.filter(p => papersWithPrice.has(p));
            if (papersWithPriceFiltered.length > 0) {
              filteredPapers = papersWithPriceFiltered;
            }
          }

          // size_only（伝票印刷）の場合はマスタフィルタリングをスキップ
          // サイズ値（B5, A5等）は「用紙」マスタに登録されていないため
          if (!isSizeOnly && ValueOrder && ValueOrder.isLoaded()) {
            const masterPapers = ValueOrder.getOrder('用紙', filteredPapers);
            if (masterPapers && masterPapers.length > 0) {
              // メタオブジェクトに登録されている用紙のみを、その順序で表示
              // Shopifyにある用紙 AND メタオブジェクトにある用紙 の交差を取る
              const intersectedPapers = masterPapers.filter(p => filteredPapers.includes(p));
              console.log('[VariantSelectors] Paper filtering (using metaobject order):', {
                shopifyPapersCount: papers.size,
                masterPapersCount: masterPapers.length,
                resultCount: intersectedPapers.length,
                resultFirst5: intersectedPapers.slice(0, 5)
              });
              // フィルタリング結果がShopifyの用紙数より大幅に少ない場合はフィルタリングしない
              // （メタオブジェクトに登録されていない用紙タイプがある場合）
              if (intersectedPapers.length >= filteredPapers.length * 0.5 || intersectedPapers.length >= 3) {
                filteredPapers = intersectedPapers;
              } else {
                // フィルタリング結果が少なすぎる場合は、ソートのみ適用
                console.log('[VariantSelectors] Skipping metaobject filter - result too small, using all Shopify papers');
                filteredPapers = ValueOrder.sortValues(filteredPapers, '用紙');
              }
            } else {
              // メタオブジェクトがない場合は従来のソートを適用
              filteredPapers = ValueOrder.sortValues(filteredPapers, '用紙');
            }
          }

          // モーダルタイトルにも表示名を使用
          if (window.PriceCalculator.Modal) {
            window.PriceCalculator.Modal.open(paperDisplayName, filteredPapers, latestPaper, window.selectPaper);
          }
        };
      }
    },

    /**
     * ページ数セレクターの初期化
     */
    initializePageCountSelector: function(shopifyOptions) {
      // ページ数オプションを生成
      let dynamicPageCounts = this.generatePageCountOptions(shopifyOptions);

      const hasPageCountFromPriceData = dynamicPageCounts.length > 0;
      const hasPageCountOptions = hasPageCountFromPriceData || 
        (window.pageCountOptions && window.pageCountOptions.options && window.pageCountOptions.options.length > 0);

      if (!hasPageCountOptions) return;

      const pageCountBtn = document.getElementById('page-count-select-btn');
      const pageCountValueSpan = document.getElementById('page-count-current-value');

      let pageCountOpts;
      let defaultPageCount;

      if (hasPageCountFromPriceData) {
        pageCountOpts = dynamicPageCounts.map(pc => ({ value: pc, label: pc + 'ページ' }));
        defaultPageCount = dynamicPageCounts[0];
      } else {
        pageCountOpts = window.pageCountOptions.options.map(val => {
          if (typeof val === 'object' && val !== null) {
            const numVal = parseInt(val.value) || val.value;
            return { value: numVal, label: val.label || (numVal + 'ページ') };
          }
          return { value: val, label: val + 'ページ' };
        });
        const rawDefault = window.pageCountOptions.default || window.pageCountOptions.options[0];
        defaultPageCount = (typeof rawDefault === 'object' && rawDefault !== null) 
          ? (parseInt(rawDefault.value) || rawDefault.value) 
          : rawDefault;
      }

      const finalPageCount = this.determineFinalPageCount(pageCountOpts, defaultPageCount, shopifyOptions);


      // UI要素の有無に関係なく、pageCountを設定
      if (window.priceCalculator?.currentSelection) {
        window.priceCalculator.currentSelection.pageCount = finalPageCount;
      }

      if (pageCountBtn && pageCountValueSpan) {
        this.setupPageCountButton(pageCountBtn, pageCountValueSpan, pageCountOpts, defaultPageCount, finalPageCount, shopifyOptions);
      }
    },

    /**
     * ページ数オプションを生成（実際の価格データでフィルタリング）
     */
    generatePageCountOptions: function(shopifyOptions) {
      let dynamicPageCounts = [];

      // Shopifyの「ページ数」オプションから直接取得（FSC中綴じ等）
      const pageCountOption = shopifyOptions.find(opt => opt.name === 'ページ数');
      if (pageCountOption && pageCountOption.values && pageCountOption.values.length > 0) {
        dynamicPageCounts = pageCountOption.values
          .map(v => parseInt(v.replace(/P$/i, '')))
          .filter(n => !isNaN(n))
          .sort((a, b) => a - b);
        if (dynamicPageCounts.length > 0) {
          return dynamicPageCounts;
        }
      }

      // window.shopifyPageCountValuesからも取得を試みる
      if (window.shopifyPageCountValues && window.shopifyPageCountValues.length > 0) {
        dynamicPageCounts = window.shopifyPageCountValues
          .map(v => parseInt(v.replace(/P$/i, '')))
          .filter(n => !isNaN(n))
          .sort((a, b) => a - b);
        if (dynamicPageCounts.length > 0) {
          return dynamicPageCounts;
        }
      }

      // Shopifyの「ページ範囲」オプションから全ページ数を生成
      const pageRangeOption = shopifyOptions.find(opt => opt.name === 'ページ範囲');
      if (pageRangeOption && pageRangeOption.values && pageRangeOption.values.length > 0) {
        const allPageCounts = new Set();
        for (const range of pageRangeOption.values) {
          const match = range.match(/^(\d+)-(\d+)P?$/);
          if (match) {
            const min = parseInt(match[1]);
            const max = parseInt(match[2]);
            for (let p = min; p <= max; p += 2) {
              allPageCounts.add(p);
            }
          }
        }

        // 現在の選択状態を取得
        const currentPaper = window.priceCalculator?.currentSelection?.paper || '';
        const currentPrintColor = window.priceCalculator?.currentSelection?.printColor || '';
        const currentCoverPaper = window.priceCalculator?.currentSelection?.coverPaper || '';
        const currentSize = window.priceCalculator?.currentSelection?.size || '';

        // 価格データを取得
        const allPriceData = window.productData?.price_matrix?.price_data || [];
        
        // 現在のvariation_codeを構築（paginated-price-table.jsのロジックに合わせる）
        let currentVariationCode = '';
        
        // 無線綴じ冊子の場合（coverPaperがある場合）
        if (currentCoverPaper) {
          currentVariationCode = currentCoverPaper;
        } else if (currentPaper || currentPrintColor) {
          // 通常の商品の場合
          const variant1 = [currentPaper, currentPrintColor].filter(p => p && p !== '' && p !== 'null').join('_');
          const variant2 = [currentPrintColor, currentPaper].filter(p => p && p !== '' && p !== 'null').join('_');
          
          // 実際のデータに存在する方を使用
          const sampleVariationCodes = [...new Set(allPriceData.map(p => p.variation_code).filter(v => v && v !== '標準'))];
          if (sampleVariationCodes.includes(variant1)) {
            currentVariationCode = variant1;
          } else if (sampleVariationCodes.includes(variant2)) {
            currentVariationCode = variant2;
          } else if (variant1) {
            currentVariationCode = variant1;
          } else {
            currentVariationCode = '標準';
          }
        }

        // 価格データに存在するページ数を抽出
        const availablePageCounts = new Set();

        // ★修正: hasSizeInDataをループの外で1回だけ計算（O(n²) → O(n)に改善）
        const hasSizeInData = allPriceData.some(p => p.size !== undefined);

        allPriceData.forEach(priceEntry => {
          // variation_codeが一致するデータのみ対象
          if (priceEntry.variation_code === currentVariationCode || currentVariationCode === '標準' || !currentVariationCode) {
            // sizeがある場合は、サイズも一致する必要がある
            if (hasSizeInData && priceEntry.size !== undefined && currentSize && priceEntry.size !== currentSize) {
              return; // サイズが一致しない場合はスキップ
            }

            // ページ数がある場合は追加
            if (priceEntry.page_count !== undefined && priceEntry.page_count !== null) {
              availablePageCounts.add(priceEntry.page_count);
            }
          }
        });

        // 理論上のページ数と実際のページ数の交差を取る
        const filteredPageCounts = [...allPageCounts].filter(pc => availablePageCounts.has(pc));
        dynamicPageCounts = filteredPageCounts.sort((a, b) => a - b);

        if (dynamicPageCounts.length > 0) {
        } else {
          // フィルタリングで0件になった場合は、全ページ数を表示（フォールバック）
          dynamicPageCounts = [...allPageCounts].sort((a, b) => a - b);
        }
      }

      // フォールバック: 価格データから直接取得
      if (dynamicPageCounts.length === 0 && window.productData?.price_matrix?.price_data) {
        const priceData = window.productData.price_matrix.price_data;
        dynamicPageCounts = [...new Set(priceData
          .filter(p => p.page_count !== undefined)
          .map(p => p.page_count)
        )].sort((a, b) => a - b);
      }

      return dynamicPageCounts;
    },

    /**
     * 最終的なページ数を決定
     */
    determineFinalPageCount: function(pageCountOpts, defaultPageCount, shopifyOptions) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlPageCount = urlParams.get('pageCount');
      const initialPageCount = urlPageCount ? parseInt(urlPageCount) : parseInt(defaultPageCount);

      const validPageCounts = pageCountOpts.map(opt => parseInt(opt.value));
      let finalPageCount = validPageCounts.includes(initialPageCount) ? initialPageCount : parseInt(defaultPageCount);

      // 本文紙質がある商品の場合はフォールバックしない
      const hasBodyPaperOption = shopifyOptions.some(opt => opt.name === '本文紙質');

      if (!hasBodyPaperOption) {
        const priceDataPageCounts = window.productData?.price_matrix?.price_data
          ? [...new Set(window.productData.price_matrix.price_data
              .filter(p => p.page_count !== undefined)
              .map(p => p.page_count)
            )].sort((a, b) => a - b)
          : [];

        if (priceDataPageCounts.length > 0 && !priceDataPageCounts.includes(finalPageCount)) {
          const fallbackPageCount = priceDataPageCounts[0];
          finalPageCount = fallbackPageCount;
        }
      } else {
      }

      return finalPageCount;
    },

    /**
     * ページ数ボタンの設定
     */
    setupPageCountButton: function(btn, valueSpan, pageCountOpts, defaultPageCount, finalPageCount, shopifyOptions) {
      valueSpan.textContent = finalPageCount + 'ページ';

      const pageCountSection = document.getElementById('page-count-section');
      if (pageCountSection) pageCountSection.style.display = '';

      if (pageCountOpts.length <= 1) {
        btn.style.opacity = '0.7';
        btn.style.cursor = 'default';
        btn.onclick = null;
      } else {
        btn.style.opacity = '';
        btn.style.cursor = 'pointer';
        btn.onclick = () => {
          const latestPageCount = window.priceCalculator?.currentSelection?.pageCount || parseInt(defaultPageCount);
          const allPageCountOpts = this.getAllPageCountOptions(pageCountOpts, shopifyOptions);
          const pageLabels = allPageCountOpts.map(opt => opt.label);
          const currentLabel = latestPageCount + 'ページ';
          if (window.PriceCalculator.Modal) {
            window.PriceCalculator.Modal.open('ページ数', pageLabels, currentLabel, window.selectPageCount);
          }
        };
      }

      const urlParams = new URLSearchParams(window.location.search);
      const urlPageCount = urlParams.get('pageCount');
      if (urlPageCount && finalPageCount !== parseInt(defaultPageCount)) {
        setTimeout(() => {
          if (typeof window.updateUI === 'function') window.updateUI();
        }, 50);
      }
    },

    /**
     * 全ページ数オプションを取得（実際の価格データでフィルタリング）
     */
    getAllPageCountOptions: function(pageCountOpts, shopifyOptions) {
      const pageRangeOpt = shopifyOptions.find(opt => opt.name === 'ページ範囲');
      if (pageRangeOpt && pageRangeOpt.values && pageRangeOpt.values.length > 0) {
        // まず、理論上の全ページ数を生成
        const allPageCounts = new Set();
        for (const range of pageRangeOpt.values) {
          const match = range.match(/^(\d+)-(\d+)P?$/);
          if (match) {
            const min = parseInt(match[1]);
            const max = parseInt(match[2]);
            for (let p = min; p <= max; p += 2) {
              allPageCounts.add(p);
            }
          }
        }

        // 現在の選択状態を取得
        const currentPaper = window.priceCalculator.currentSelection.paper || '';
        const currentPrintColor = window.priceCalculator.currentSelection.printColor || '';
        const currentCoverPaper = window.priceCalculator.currentSelection.coverPaper || '';
        const currentSize = window.priceCalculator.currentSelection.size || '';

        // 価格データを取得
        const allPriceData = window.productData?.price_matrix?.price_data || [];
        
        // 現在のvariation_codeを構築（paginated-price-table.jsのロジックに合わせる）
        let currentVariationCode = '';
        
        // 無線綴じ冊子の場合（coverPaperがある場合）
        if (currentCoverPaper) {
          currentVariationCode = currentCoverPaper;
        } else if (currentPaper || currentPrintColor) {
          // 通常の商品の場合
          const variant1 = [currentPaper, currentPrintColor].filter(p => p && p !== '' && p !== 'null').join('_');
          const variant2 = [currentPrintColor, currentPaper].filter(p => p && p !== '' && p !== 'null').join('_');
          
          // 実際のデータに存在する方を使用
          const sampleVariationCodes = [...new Set(allPriceData.map(p => p.variation_code).filter(v => v && v !== '標準'))];
          if (sampleVariationCodes.includes(variant1)) {
            currentVariationCode = variant1;
          } else if (sampleVariationCodes.includes(variant2)) {
            currentVariationCode = variant2;
          } else if (variant1) {
            currentVariationCode = variant1;
          } else {
            currentVariationCode = '標準';
          }
        }


        // 価格データに存在するページ数を抽出
        const availablePageCounts = new Set();

        // ★修正: hasSizeInDataをループの外で1回だけ計算（O(n²) → O(n)に改善）
        const hasSizeInData = allPriceData.some(p => p.size !== undefined);

        allPriceData.forEach(priceEntry => {
          // variation_codeが一致するデータのみ対象
          if (priceEntry.variation_code === currentVariationCode || currentVariationCode === '標準' || !currentVariationCode) {
            // sizeがある場合は、サイズも一致する必要がある
            if (hasSizeInData && priceEntry.size !== undefined && currentSize && priceEntry.size !== currentSize) {
              return; // サイズが一致しない場合はスキップ
            }

            // ページ数がある場合は追加
            if (priceEntry.page_count !== undefined && priceEntry.page_count !== null) {
              availablePageCounts.add(priceEntry.page_count);
            }
          }
        });

        // 理論上のページ数と実際のページ数の交差を取る
        const filteredPageCounts = [...allPageCounts].filter(pc => availablePageCounts.has(pc));
        const sortedPageCounts = filteredPageCounts.sort((a, b) => a - b);

        if (sortedPageCounts.length > 0) {
          return sortedPageCounts.map(pc => ({ value: pc, label: pc + 'ページ' }));
        } else {
          console.warn('[VariantSelectors] No page counts available for current variation, showing all');
          // フィルタリングで0件になった場合は、全ページ数を表示（フォールバック）
          const allSortedPageCounts = [...allPageCounts].sort((a, b) => a - b);
          return allSortedPageCounts.map(pc => ({ value: pc, label: pc + 'ページ' }));
        }
      }
      return pageCountOpts;
    },

    /**
     * ハーフカットセレクターの初期化（ステッカー商品用）
     */
    initializeHalfCutSelector: function() {
      const halfCutSection = document.getElementById('half-cut-section');
      const halfCutBtn = document.getElementById('half-cut-select-btn');
      const halfCutValueSpan = document.getElementById('half-cut-current-value');

      // Shopifyオプションから「ハーフカット」を検出
      const shopifyOptions = window.productData?.shopify_options || [];
      const halfCutOption = shopifyOptions.find(opt => opt.name === 'ハーフカット');

      if (!halfCutOption || !halfCutOption.values || halfCutOption.values.length === 0) {
        if (halfCutSection) halfCutSection.style.display = 'none';
        return;
      }

      // セクションを表示
      if (halfCutSection) halfCutSection.style.display = '';

      // 並び順マスタでソート
      let sortedHalfCuts = [...halfCutOption.values];
      const ValueOrder = window.PriceCalculator?.ValueOrder;
      if (ValueOrder && ValueOrder.isLoaded()) {
        sortedHalfCuts = ValueOrder.sortValues(sortedHalfCuts, 'ハーフカット');
      }

      // デフォルト値を設定
      const defaultHalfCut = sortedHalfCuts[0];
      if (!window.priceCalculator.currentSelection.halfCut) {
        window.priceCalculator.currentSelection.halfCut = defaultHalfCut;
      }

      if (!halfCutBtn || !halfCutValueSpan) return;

      // 現在値を表示
      const currentHalfCut = window.priceCalculator.currentSelection.halfCut || defaultHalfCut;
      halfCutValueSpan.classList.remove('skeleton-loading');
      halfCutValueSpan.textContent = currentHalfCut;

      // ボタンの設定
      if (sortedHalfCuts.length <= 1) {
        halfCutBtn.style.opacity = '0.7';
        halfCutBtn.style.cursor = 'default';
        halfCutBtn.onclick = null;
      } else {
        halfCutBtn.style.opacity = '';
        halfCutBtn.style.cursor = 'pointer';
        halfCutBtn.onclick = () => {
          const latestHalfCut = window.priceCalculator.currentSelection.halfCut || defaultHalfCut;
          if (window.PriceCalculator.Modal) {
            window.PriceCalculator.Modal.open('ハーフカット', sortedHalfCuts, latestHalfCut, window.selectHalfCut);
          }
        };
      }

    },

    /**
     * ページ範囲セレクターの初期化
     */
    initializePageRangeSelector: function() {
      const pageRangeBtn = document.getElementById('page-range-select-btn');
      const pageRangeValueSpan = document.getElementById('page-range-current-value');
      const pageRangeSection = document.getElementById('page-range-section');

      // window.pageRangeOptionsが設定されていない場合は非表示
      if (!window.pageRangeOptions || window.pageRangeOptions.length === 0) {
        if (pageRangeSection) pageRangeSection.style.display = 'none';
        return;
      }

      // セクションを表示
      if (pageRangeSection) pageRangeSection.style.display = '';

      // ページ範囲オプションを昇順にソート（最小ページ数で比較）
      const sortedPageRanges = [...window.pageRangeOptions].sort((a, b) => {
        const matchA = a.match(/^(\d+)/);
        const matchB = b.match(/^(\d+)/);
        const minA = matchA ? parseInt(matchA[1]) : 0;
        const minB = matchB ? parseInt(matchB[1]) : 0;
        return minA - minB;
      });

      // デフォルト値を設定（ソート後の最初の値 = 最小ページ範囲）
      const defaultPageRange = sortedPageRanges[0];
      let minPage = 8; // デフォルト値
      if (!window.priceCalculator.currentSelection.pageRange) {
        window.priceCalculator.currentSelection.pageRange = defaultPageRange;

        // ページ範囲から最小ページ数を計算（単一ページ形式にも対応）
        const match = defaultPageRange.match(/^(\d+)(?:[-〜～](\d+))?(?:P|ページ〜?)?$/);
        if (match) {
          minPage = parseInt(match[1]);
          window.priceCalculator.currentSelection.pageCount = minPage;
        }
      } else {
        // 既に設定されている場合は現在のページ数を取得
        minPage = window.priceCalculator.currentSelection.pageCount || minPage;
      }

      if (!pageRangeBtn || !pageRangeValueSpan) return;

      // 現在値を表示
      const currentPageRange = window.priceCalculator.currentSelection.pageRange || defaultPageRange;
      pageRangeValueSpan.textContent = currentPageRange;

      // ★ 追加: 初期化時にページ数セレクターのオプションを連動させる
      const pageCountOptions = this.getPageCountOptionsForRange(currentPageRange);
      if (pageCountOptions.length > 0) {
        this.updatePageCountOptions(pageCountOptions, minPage);
        console.log('[VariantSelectors] initializePageRangeSelector - Page count options set:', {
          pageRange: currentPageRange,
          options: pageCountOptions,
          selected: minPage
        });
      }

      // ボタンの設定
      if (sortedPageRanges.length <= 1) {
        pageRangeBtn.style.opacity = '0.7';
        pageRangeBtn.style.cursor = 'default';
        pageRangeBtn.onclick = null;
      } else {
        pageRangeBtn.style.opacity = '';
        pageRangeBtn.style.cursor = 'pointer';
        pageRangeBtn.onclick = () => {
          const latestPageRange = window.priceCalculator.currentSelection.pageRange || defaultPageRange;
          // ソートされたページ範囲リストを使用
          const sortedOptions = [...window.pageRangeOptions].sort((a, b) => {
            const matchA = a.match(/^(\d+)/);
            const matchB = b.match(/^(\d+)/);
            const minA = matchA ? parseInt(matchA[1]) : 0;
            const minB = matchB ? parseInt(matchB[1]) : 0;
            return minA - minB;
          });
          if (window.PriceCalculator.Modal) {
            window.PriceCalculator.Modal.open('ページ範囲', sortedOptions, latestPageRange, window.selectPageRange);
          }
        };
      }
    },

    /**
     * 印刷色ボタンの初期化
     */
    initializePrintColorButton: function(papers, printColors) {
      if (printColors.size === 0 || papers.size === 0) return;

      const combinationsForColor = window.availableVariantCombinations || window.productData?.availableVariantCombinations;
      const currentPaperForColor = window.priceCalculator.currentSelection.paper || Array.from(papers)[0];
      let validColorsForModal = Array.from(printColors);

      if (combinationsForColor && combinationsForColor.byPaper && combinationsForColor.byPaper[currentPaperForColor]) {
        validColorsForModal = combinationsForColor.byPaper[currentPaperForColor];
      }

      const currentColor = window.priceCalculator.currentSelection.printColor || validColorsForModal[0];
      const colorBtn = document.getElementById('print-color-select-btn');
      const colorValueSpan = document.getElementById('print-color-current-value');
      const colorLabelSpan = document.getElementById('print-color-option-label');

      if (!colorBtn || !colorValueSpan) return;

      colorValueSpan.classList.remove('skeleton-loading');
      colorValueSpan.textContent = currentColor;

      // 印刷色数オプションの表示名を取得（メタオブジェクトから）
      const ValueOrder = window.PriceCalculator?.ValueOrder;
      const colorDisplayName = ValueOrder?.getDisplayName?.('印刷色数', validColorsForModal) || '印刷色数';

      // サイドバーのラベルを更新
      if (colorLabelSpan) {
        colorLabelSpan.textContent = colorDisplayName;
      }

      if (validColorsForModal.length <= 1) {
        colorBtn.style.opacity = '0.7';
        colorBtn.style.cursor = 'default';
        colorBtn.onclick = null;
      } else {
        colorBtn.style.opacity = '';
        colorBtn.style.cursor = 'pointer';
        colorBtn.onclick = () => {
          const latestPaper = window.priceCalculator.currentSelection.paper;
          const latestCombinations = window.availableVariantCombinations || window.productData?.availableVariantCombinations;

          // withPriceByPaper を優先（価格データがある印刷色数のみ）
          let latestValidColors;
          if (latestCombinations && latestCombinations.withPriceByPaper && latestCombinations.withPriceByPaper[latestPaper]) {
            latestValidColors = [...latestCombinations.withPriceByPaper[latestPaper]];
          } else if (latestCombinations && latestCombinations.byPaper && latestCombinations.byPaper[latestPaper]) {
            // フォールバック: 全バリアントの組み合わせ
            latestValidColors = [...latestCombinations.byPaper[latestPaper]];
          } else {
            latestValidColors = Array.from(printColors);
          }

          // 並び順マスタでソート（PrintHubで設定された順序を適用）
          if (ValueOrder && ValueOrder.isLoaded()) {
            latestValidColors = ValueOrder.sortValues(latestValidColors, '印刷色数');
          }

          const latestColor = window.priceCalculator.currentSelection.printColor || currentColor;
          // モーダルタイトルにも表示名を使用
          if (window.PriceCalculator.Modal) {
            window.PriceCalculator.Modal.open(colorDisplayName, latestValidColors, latestColor, window.selectPrintColor);
          }
        };
      }
    },

    /**
     * ページ数オプションをリフレッシュ（用紙や印刷色が変更された時用）
     */
    refreshPageCountOptions: function() {
      const shopifyOptions = window.productData?.shopify_options || [];
      const pageCountBtn = document.getElementById('page-count-select-btn');
      if (!pageCountBtn) return;

      const btn = pageCountBtn;

      // 価格データからページ数オプションを再生成
      const dynamicPageCounts = this.generatePageCountOptions(shopifyOptions);
      const hasPageCountFromPriceData = dynamicPageCounts.length > 0;
      const hasLegacyPageCountOptions = window.pageCountOptions && window.pageCountOptions.options && window.pageCountOptions.options.length > 0;

      if (!hasPageCountFromPriceData && !hasLegacyPageCountOptions) return;

      let pageCountOpts;
      let defaultPageCount;

      if (hasPageCountFromPriceData) {
        pageCountOpts = dynamicPageCounts.map(pc => ({ value: pc, label: pc + 'ページ' }));
        defaultPageCount = dynamicPageCounts[0];
      } else {
        pageCountOpts = window.pageCountOptions.options.map(val => {
          if (typeof val === 'object' && val !== null) {
            const numVal = parseInt(val.value) || val.value;
            return { value: numVal, label: val.label || (numVal + 'ページ') };
          }
          return { value: val, label: val + 'ページ' };
        });
        defaultPageCount = window.pageCountOptions.default || window.pageCountOptions.options[0];
      }

      const finalDefault = (typeof defaultPageCount === 'object' && defaultPageCount !== null)
        ? (parseInt(defaultPageCount.value) || defaultPageCount.value)
        : defaultPageCount;

      if (pageCountOpts.length <= 1) {
        btn.style.opacity = '0.7';
        btn.style.cursor = 'default';
        btn.onclick = null;
      } else {
        btn.style.opacity = '';
        btn.style.cursor = 'pointer';
        btn.onclick = () => {
          const latestPageCount = window.priceCalculator?.currentSelection?.pageCount || parseInt(finalDefault);
          const allPageCountOpts = this.getAllPageCountOptions(pageCountOpts, shopifyOptions);
          const pageLabels = allPageCountOpts.map(opt => opt.label);
          const currentLabel = latestPageCount + 'ページ';
          if (window.PriceCalculator.Modal) {
            window.PriceCalculator.Modal.open('ページ数', pageLabels, currentLabel, window.selectPageCount);
          }
        };
      }
    },

    /**
     * ページ範囲からページ数オプションを生成
     * price_matrixの実データから動的に刻み幅を判断
     * @param {string} pageRange - "8-28P", "32-52P", "24〜32ページ" など
     * @returns {Array<number>} - [8, 12, 16, ..., 28] or [24, 26, 28, 30]
     */
    getPageCountOptionsForRange: function(pageRange) {
      if (!pageRange) return [];

      // "24-32P", "24〜32ページ", "124ページ"（単一ページ）形式に対応
      const match = pageRange.match(/^(\d+)(?:[-〜～](\d+))?(?:P|ページ〜?)?$/);
      if (!match) return [];

      const min = parseInt(match[1]);
      const max = match[2] ? parseInt(match[2]) : min; // 単一ページの場合はmin=max

      // price_matrixのデータから該当ページ範囲のpage_countを取得
      const priceData = window.productData?.price_matrix?.price_data || [];
      const pageCountsFromData = [...new Set(
        priceData
          .filter(p => p.page_count !== undefined && p.page_count >= min && p.page_count <= max)
          .map(p => p.page_count)
      )].sort((a, b) => a - b);

      // データがあればそれを使用
      if (pageCountsFromData.length >= 2) {
        return pageCountsFromData;
      }

      // フォールバック: データがない場合は刻み幅を推測
      // データから刻み幅を計算（最初の2つの差分）
      let increment = 4; // デフォルトは4ページ刻み
      if (priceData.length > 0) {
        const allPageCounts = [...new Set(priceData.filter(p => p.page_count !== undefined).map(p => p.page_count))].sort((a, b) => a - b);
        if (allPageCounts.length >= 2) {
          increment = allPageCounts[1] - allPageCounts[0];
          if (increment !== 2 && increment !== 4) {
            increment = 4; // 不正な値の場合はデフォルト
          }
        }
      }

      const pageCounts = [];
      for (let p = min; p <= max; p += increment) {
        pageCounts.push(p);
      }

      return pageCounts;
    },

    /**
     * ページ数セレクターのオプションを更新（ページ範囲連動）
     * @param {Array<number>} pageCountOptions - 利用可能なページ数の配列
     * @param {number} selectedPageCount - 選択するページ数
     */
    updatePageCountOptions: function(pageCountOptions, selectedPageCount) {
      const pageCountBtn = document.getElementById('page-count-select-btn');
      const pageCountValueSpan = document.getElementById('page-count-current-value');

      if (!pageCountBtn || !pageCountValueSpan) return;

      // 表示を更新
      pageCountValueSpan.textContent = selectedPageCount + 'ページ';

      // 選択肢が1つ以下ならボタンを無効化
      if (pageCountOptions.length <= 1) {
        pageCountBtn.style.opacity = '0.7';
        pageCountBtn.style.cursor = 'default';
        pageCountBtn.onclick = null;
        return;
      }

      // ボタンを有効化してクリックハンドラーを設定
      pageCountBtn.style.opacity = '';
      pageCountBtn.style.cursor = 'pointer';

      const self = this;
      pageCountBtn.onclick = () => {
        const currentPageRange = window.priceCalculator?.currentSelection?.pageRange;
        let optionsToShow = pageCountOptions;

        // 現在のページ範囲からオプションを再取得（最新の状態を反映）
        if (currentPageRange) {
          optionsToShow = self.getPageCountOptionsForRange(currentPageRange);
        }

        const pageLabels = optionsToShow.map(p => p + 'ページ');
        const latestPageCount = window.priceCalculator?.currentSelection?.pageCount || selectedPageCount;
        const currentLabel = latestPageCount + 'ページ';

        if (window.PriceCalculator.Modal) {
          window.PriceCalculator.Modal.open('ページ数', pageLabels, currentLabel, window.selectPageCount);
        }
      };

      console.log('[VariantSelectors] Page count options updated:', {
        pageRange: window.priceCalculator?.currentSelection?.pageRange,
        options: pageCountOptions,
        selected: selectedPageCount
      });
    }
  };

  // モジュールをエクスポート
  window.PriceCalculator.VariantSelectors = VariantSelectors;

  // グローバル関数との互換性
  window.initializeVariantSelectors = function() {
    VariantSelectors.initialize();
  };

  window.updateVariantSelectors = function() {
    // モーダル形式では特に更新処理は不要
  };

})();

